import * as React from "react";
import { Alert, Button, Card, Modal, Popconfirm, Space, Tag } from "antd";
import { CloseOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Table } from "../../newcomponents/table/table";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { IdentityInput } from "../../schema/types/identity/input";
import { v4 as uuidv4 } from "uuid";
import { formSet, formSetValue, formTriggerValidation, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { CpInput } from "../gvc/testNewForm/CpInput";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpArrayString } from "../gvc/testNewForm/CpArrayString";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { getDefaultKindSelectState } from "../../components/antd/kindselect/KindSelect";
import { CpKindSelect } from "../gvc/testNewForm/temp/CpKindSelect";

export const IdentityCreateNetworkResourceIP: React.FC = () => {
  const { id } = useCpFormContext();

  const isAddingItem = useCpFormValue(`networkResources.ipItemForm.isAddingItem`);
  const setIsAddingItem = (value: string | boolean) => {
    formSetValue(id, `networkResources.ipItemForm.isAddingItem`, value);
  };

  const ipResources = useCpFormValue(`networkResources.ip`);
  let filteredIpResources = ipResources;
  filteredIpResources = filteredIpResources.filter((r) => {
    if (isAddingItem !== true && isAddingItem !== false) {
      return r.id !== isAddingItem;
    }
    return true;
  });
  const fqdnResources = useCpFormValue(`networkResources.fqdn`);
  const awsResources = useCpFormValue(`nativeNetworkResources.aws`);
  const gcpResources = useCpFormValue(`nativeNetworkResources.gcp`);

  const allResourcesFiltered = [...filteredIpResources, ...fqdnResources, ...awsResources, ...gcpResources];
  const allNames = React.useMemo(() => allResourcesFiltered.map((r) => r.name).filter(Boolean), [
    allResourcesFiltered,
  ]) as string[];

  const onDoneItemForm = async () => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const itemForm = cloneDeep(values.networkResources.ipItemForm);
    let currentIpArray = cloneDeep(values.networkResources.ip);
    if (typeof isAddingItem !== "boolean") {
      currentIpArray = currentIpArray.filter((r) => r.id !== isAddingItem);
    }

    currentIpArray.push({
      id: uuidv4(),
      name: itemForm.name,
      agent: itemForm.agent,
      ports: itemForm.ports.map((p) => p.value),
      ips: itemForm.ips.map((i) => i.value),
    });

    formSetValue(id, `networkResources.ip`, currentIpArray);
    formSet(id, `fieldStates.networkResources.ipItemForm.name.simulateTouched`, false);
    formSet(id, `fieldStates.networkResources.ipItemForm.agent.simulateTouched`, false);
    formSet(id, `fieldStates.networkResources.ipItemForm.ipsroot.simulateTouched`, false);
    formSet(id, `fieldStates.networkResources.ipItemForm.portsroot.simulateTouched`, false);
    formTriggerValidation(id);
    setIsAddingItem(false);
  };

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const currentIpArray = cloneDeep(values.networkResources.ip);
    currentIpArray.splice(index, 1);
    formSetValue(id, `networkResources.ip`, currentIpArray);
    formTriggerValidation(id);
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>IP Resources</CardTitle>
          <CardContent>
            {ipResources.length > 0 ? (
              <Table<any>
                tableId="ipResourcesEditable-update-3"
                data={ipResources}
                hideSettings
                columns={[
                  { id: "name", label: "Name", enableResize: true, size: 200 },
                  { id: "agent.value", label: "Agent", enableResize: true, size: 200 },
                  {
                    id: "ports",
                    label: "Ports",
                    enableResize: true,
                    size: 200,
                    cell: ({ row }) => {
                      const ports = row.original.ports || [];
                      return (
                        <div className="flex items-center">
                          {ports.map((port) => (
                            <Tag key={port}>{port}</Tag>
                          ))}
                        </div>
                      );
                    },
                  },
                  {
                    id: "ips",
                    label: "IPs",
                    enableResize: true,
                    size: 200,
                    cell: ({ row }) => {
                      const ips = row.original.ips || [];
                      return (
                        <div className="flex items-center">
                          {ips.map((ip) => (
                            <Tag key={ip}>{ip}</Tag>
                          ))}
                        </div>
                      );
                    },
                  },
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Space.Compact>
                            <Button
                              htmlType={`button`}
                              onClick={() => {
                                const item = ipResources.find((r) => r.id === row.original.id);
                                if (item) {
                                  formSetValue(id, `networkResources.ipItemForm`, {
                                    isAddingItem: row.original.id,
                                    agent: item.agent,
                                    name: item.name,
                                    ips: item.ips.map((i) => ({ id: uuidv4(), value: i })),
                                    ports: item.ports.map((p) => ({ id: uuidv4(), value: p })),
                                  });
                                }
                              }}
                              type={"text"}
                              icon={<EditOutlined />}
                            />
                            <Popconfirm title={"Confirm"} onConfirm={() => onRemoveItem(row.index)}>
                              <Button htmlType={`button`} type={"text"} icon={<CloseOutlined />} danger />
                            </Popconfirm>
                          </Space.Compact>
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            {!isAddingItem ? null : (
              <ItemFormContent
                isAddingItem={isAddingItem}
                allNames={allNames}
                onCancel={() => {
                  // Clear simulateTouched states
                  formSet(id, `fieldStates.networkResources.ipItemForm.name.simulateTouched`, false);
                  formSet(id, `fieldStates.networkResources.ipItemForm.agent.simulateTouched`, false);
                  formSet(id, `fieldStates.networkResources.ipItemForm.ipsroot.simulateTouched`, false);
                  formSet(id, `fieldStates.networkResources.ipItemForm.portsroot.simulateTouched`, false);
                  formSetValue(id, `networkResources.ipItemForm`, {
                    isAddingItem: false,
                    name: "",
                    agent: getDefaultKindSelectState(),
                    ips: [],
                    ports: [],
                  });
                }}
                onDone={onDoneItemForm}
              />
            )}
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => {
                formSetValue(id, `networkResources.ipItemForm`, {
                  isAddingItem: true,
                  name: "",
                  agent: getDefaultKindSelectState(),
                  ips: [],
                  ports: [],
                });
              }}
            >
              Add IP
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

interface Props {
  isAddingItem: boolean | string;
  allNames: string[];
  onCancel: any;
  onDone: any;
}
const ItemFormContent: React.FC<Props> = ({ isAddingItem, allNames, onCancel, onDone }) => {
  const { id } = useCpFormContext();
  const name = useCpFormValue(`networkResources.ipItemForm.name`);

  const agentValue = useCpFormValue(`networkResources.ipItemForm.agent.value`);

  const ips = useCpFormValue(`networkResources.ipItemForm.ips`);
  const ports = useCpFormValue(`networkResources.ipItemForm.ports`);

  const isNameUnique = !allNames.includes(name);

  const isValid =
    name &&
    isNameUnique &&
    agentValue &&
    ips.length > 0 &&
    !ips.some((p) => !p.value) &&
    ports.length > 0 &&
    !ports.some((p) => !p.value);

  return (
    <Modal
      open={isAddingItem !== false}
      title={`${isAddingItem === true ? "Add" : "Edit"} IP Resource`}
      okText={"Add"}
      cancelText="Cancel"
      okButtonProps={{ htmlType: "submit" }}
      onCancel={onCancel}
      onOk={() => {
        // Mark all fields as simulateTouched so error states are shown
        formSet(id, `fieldStates.networkResources.ipItemForm.name.simulateTouched`, true);
        formSet(id, `fieldStates.networkResources.ipItemForm.agent.simulateTouched`, true);
        formSet(id, `fieldStates.networkResources.ipItemForm.ipsroot.simulateTouched`, true);
        formSet(id, `fieldStates.networkResources.ipItemForm.portsroot.simulateTouched`, true);
        formTriggerValidation(id);
        if (!isValid) {
          return;
        }
        onDone();
      }}
      destroyOnHidden
      closable={false}
      maskClosable={false}
    >
      {isNameUnique ? null : <Alert className="mb-2" type={`error`} message={`All "name" values must be unique`} />}
      <CpFormItem>
        <CpLabel name={`networkResources.ipItemForm.name`}>Name</CpLabel>
        <CpInput name={`networkResources.ipItemForm.name`} />
        <CpErrorWarning name={`networkResources.ipItemForm.name`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`networkResources.ipItemForm.agent`}>Agent</CpLabel>
        <CpKindSelect kind="agent" optionValue="name" name={`networkResources.ipItemForm.agent`} />
        <CpErrorWarning name={`networkResources.ipItemForm.agent`} />
      </CpFormItem>
      <CpArrayString label="IPs" name={`networkResources.ipItemForm.ips`} />
      <CpArrayString label="Ports" name={`networkResources.ipItemForm.ports`} />
    </Modal>
  );
};
